import { useEffect, useState, type FormEvent } from "react";
import {
  investors as api,
  type InvestorInput,
  type InvestorType,
} from "../../api";
import { Link, useRouter } from "../../router";
import {
  ClientTypeIcon,
  INVESTOR_TYPE_LABELS,
} from "../../components/ClientTypeIcon";

interface Props {
  mode: "create" | "edit";
  id?: number;
}

const TYPES: InvestorType[] = ["person", "company", "group"];

// The "name" field reads more naturally per type in the demo than a bare
// "Name" everywhere.
const NAME_LABEL: Record<InvestorType, string> = {
  person: "Name",
  company: "Company name",
  group: "Group name",
};

const empty: InvestorInput = {
  name: "",
  type: "person",
  org_id: "",
  email: "",
  phone: "",
  address: "",
  zip: "",
  city: "",
};

export function InvestorForm({ mode, id }: Props) {
  const { navigate } = useRouter();
  const [form, setForm] = useState<InvestorInput>(empty);
  const [loading, setLoading] = useState(mode === "edit");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "edit" || id === undefined) return;
    let cancelled = false;
    api
      .get(id)
      .then((d) => {
        if (cancelled) return;
        setForm({
          name: d.investor.name,
          type: d.investor.type,
          org_id: d.investor.org_id ?? "",
          email: d.investor.email ?? "",
          phone: d.investor.phone ?? "",
          address: d.investor.address ?? "",
          zip: d.investor.zip ?? "",
          city: d.investor.city ?? "",
        });
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, id]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name?.trim()) {
      setError("Name is required.");
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "create") {
        const { investor } = await api.create(form);
        navigate(`/app/crm/${investor.id}`);
      } else if (id !== undefined) {
        await api.update(id, form);
        navigate(`/app/crm/${id}`);
      }
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="page">
        <div className="muted">Loading…</div>
      </div>
    );
  }

  const cancelTo =
    mode === "edit" && id !== undefined ? `/app/crm/${id}` : "/app/crm";

  return (
    <div className="page">
      <Link to={cancelTo} className="back-link">
        ← Cancel
      </Link>
      <header className="page-head">
        <div>
          <span className="eyebrow">CRM</span>
          <h1>{mode === "create" ? "New investor" : "Edit investor"}</h1>
        </div>
      </header>

      <form className="card-form" onSubmit={onSubmit} noValidate>
        <div className="field">
          <span>Client type</span>
          <div className="kind-picker">
            {TYPES.map((t) => (
              <button
                key={t}
                type="button"
                className={
                  form.type === t ? "kind-option active" : "kind-option"
                }
                onClick={() => setForm({ ...form, type: t })}
                aria-pressed={form.type === t}
              >
                <span className="glyph">
                  <ClientTypeIcon type={t} size={18} />
                </span>
                {INVESTOR_TYPE_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
        <label className="field">
          <span>{NAME_LABEL[form.type ?? "person"]} *</span>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            autoFocus
          />
        </label>
        {form.type === "company" && (
          <label className="field">
            <span>Organisation number</span>
            <input
              type="text"
              value={form.org_id ?? ""}
              onChange={(e) => setForm({ ...form, org_id: e.target.value })}
              placeholder="e.g. 923 609 016"
            />
          </label>
        )}
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            value={form.email ?? ""}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </label>
        <label className="field">
          <span>Phone</span>
          <input
            type="tel"
            value={form.phone ?? ""}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </label>
        <label className="field">
          <span>Address</span>
          <input
            type="text"
            value={form.address ?? ""}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            placeholder="Street and number"
          />
        </label>
        <div className="field-row">
          <label className="field field-zip">
            <span>Zip</span>
            <input
              type="text"
              value={form.zip ?? ""}
              onChange={(e) => setForm({ ...form, zip: e.target.value })}
            />
          </label>
          <label className="field field-grow">
            <span>City</span>
            <input
              type="text"
              value={form.city ?? ""}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </label>
        </div>

        {error && <div className="banner-error">{error}</div>}

        <div className="form-actions">
          <Link to={cancelTo} className="btn btn-ghost">
            Cancel
          </Link>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting
              ? "Saving…"
              : mode === "create"
                ? "Create investor"
                : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
